import prioUpdater from '../../components/shared/PrioUpdater';
import entryUpdater from '../../pages/DashboardComponents/TemplateEngine/InputFormRenderer/EntryUpdater';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import styleCache from './StyleCache';
import styleFetcher from './StyleFetcher';
import EngineCleanupBase from '../../services/engines/EngineCleanupBase';

const _name = 'BodyEditorEngine';

/**
 * BodyEditorEngine — singleton that drives the floating WYSIWYG body editor dialog.
 * Caller opens the dialog via open(entry), closes via close().
 * useBodyEditor subscribes to state changes.
 * Loads style notes into StyleCache at bootup for use by MarkdownRenderer HTML view.
 */
class BodyEditorEngine extends EngineCleanupBase {
    _entry = null;
    _isOpen = false;

    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Fetches style notes and populates StyleCache.
     * Called once via PageContent.checkSignalR — cache is read by MarkdownRenderer on every HTML render.
     */
    fetchStyles = async () => {
        try {
            styleCache.clear();

            // load Jerny.StyleNotes topic for styles
            const styles = await styleFetcher.fetch();
            if (!styles) return;

            styleCache.addOrUpdate(styles.entries);
        } catch (error) {
            this.logError('fetchStyles', error.message);
        }
    };

    /**
     * Opens the body editor with the given entry.
     * @param {object} entry
     */
    open = (entry) => {
        if (!entry) return;
        this._entry = entry;
        this._isOpen = true;
        this._notify();
    };

    close = () => {
        this._isOpen = false;
        this._entry = null;
        this._notify();
    };

    /**
     * Called by PageContentSRHandlers on OnJournalUpdated.
     * If the currently open entry is in the update batch, refreshes it and notifies.
     * @param {object[]} entries
     */
    onJournalUpdated = (entries) => {
        if (!this._isOpen || !this._entry || !Array.isArray(entries)) return;
        const updated = entries.find((e) => e.rk === this._entry.rk);
        if (!updated) return;
        this._entry = { ...updated };
        this._notify();
    };

    save = async (html) => {
        if (this._entry === null) {
            return;
        }

        await entryUpdater.savePlainTextBody(this._entry, html);
        this._entry.body = html;

        this.logDebug('save', 'saved');

        // Ensure format is 'html' — ContentPanel uses this to pick the right renderer
        if (this._entry.format === 'html') {
            return;
        }

        // set html state when not set
        await prioUpdater.save({ ...this._entry, format: 'html' }, null);
        this._entry.format = 'html';
    };

    getEntry = () => this._entry;
    isOpen = () => this._isOpen;

    _notify = () => {
        ComponentNotifier.notify(_name, 'stateChanged', {
            isOpen: this._isOpen,
            entry: this._entry,
        });
    };
}

const bodyEditorEngine = new BodyEditorEngine();
export default bodyEditorEngine;
